import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import {
  LayoutDashboard, Search, Bookmark, BarChart2,
  ShieldCheck, LogOut, User, Sun, Moon,
  ChevronDown, Zap, Users, Sparkles
} from 'lucide-react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropRef   = useRef(null);
  const mobileRef = useRef(null);

  useEffect(() => {
    const h = e => {
      if (dropRef.current   && !dropRef.current.contains(e.target))   setDropOpen(false);
      if (mobileRef.current && !mobileRef.current.contains(e.target)) setMobileOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { setMobileOpen(false); setDropOpen(false); }, [location.pathname]);

  const NAV = [
    { path: '/dashboard', label: 'Dashboard',   icon: <LayoutDashboard size={15}/> },
    { path: '/search',    label: 'Search Leads', icon: <Search size={15}/> },
    { path: '/saved',     label: 'Saved Leads',  icon: <Bookmark size={15}/> },
    { path: '/agent',     label: 'AI Agent',     icon: <Sparkles size={15}/>, aiTag: true },
    { path: '/history',   label: 'History',      icon: <BarChart2 size={15}/> },
    { path: '/team',      label: 'Team',         icon: <Users size={15}/>,
      badge: ['pro','enterprise'].includes(user?.plan) ? null : 'PRO'
    },
    ...(user?.role === 'admin'
      ? [{ path: '/admin', label: 'Admin', icon: <ShieldCheck size={15}/> }]
      : []),
  ];

  const initials = `${user?.first_name?.[0]||''}${user?.last_name?.[0]||''}`.toUpperCase();
  const isActive = path => location.pathname.startsWith(path);

  const planColor = user?.plan === 'enterprise' ? 'var(--purple)' : user?.plan === 'pro' ? 'var(--blue)' : 'var(--text3)';
  const planBg    = user?.plan === 'enterprise' ? 'var(--purple-bg)' : user?.plan === 'pro' ? 'var(--blue-bg)' : 'var(--bg3)';

  return (
    <>
      <nav className="navbar">
        {/* Brand */}
        <div className="navbar-brand" onClick={() => navigate('/dashboard')} style={{ cursor:'pointer' }}>
          <div className="brand-icon">📡</div>
          Lead<span className="navbar-brand-dot">Radar</span>
        </div>

        {/* Desktop nav links */}
        <div className="navbar-nav" style={{ display:'flex' }}>
          {NAV.map(n => (
            <button
              key={n.path}
              className={`nav-link${isActive(n.path) ? ' active' : ''}`}
              onClick={() => navigate(n.path)}
              style={{ position:'relative' }}
            >
              {n.icon} {n.label}
              {n.aiTag && (
                <span style={{
                  marginLeft:5, fontSize:9, fontWeight:800, padding:'1px 5px',
                  borderRadius:4, background:'linear-gradient(135deg,#8b5cf6,#e11d48)',
                  color:'#fff', letterSpacing:.5, verticalAlign:'middle',
                }}>
                  AI
                </span>
              )}
              {n.badge && !n.aiTag && (
                <span style={{
                  marginLeft:5, fontSize:9, fontWeight:800, padding:'1px 5px',
                  borderRadius:4, background:'var(--accent)', color:'#fff',
                  letterSpacing:.5, verticalAlign:'middle',
                }}>
                  {n.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="navbar-spacer"/>

        {/* Right side */}
        <div className="navbar-right">
          {user?.role !== 'admin' && (
            <div
              style={{ display:'flex', alignItems:'center', gap:6, background:'var(--accent-bg)', border:'1px solid var(--accent)', borderRadius:20, padding:'4px 12px', fontSize:12, color:'var(--accent)', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}
              onClick={() => navigate('/dashboard')}
              title="Search credits remaining"
            >
              <Zap size={12}/>{user?.search_credits ?? 0} credits
            </div>
          )}

          <button className="theme-toggle" onClick={toggleTheme} title="Toggle theme">
            {theme === 'dark' ? <Sun size={16}/> : <Moon size={16}/>}
          </button>

          {/* User dropdown */}
          <div className="user-menu" ref={dropRef}>
            <div
              className="user-avatar"
              onClick={() => setDropOpen(o => !o)}
              style={{ display:'flex', alignItems:'center', gap:6, borderRadius:20, width:'auto', padding:'0 10px 0 4px' }}
              title={`${user?.first_name} ${user?.last_name}`}
            >
              <div style={{ width:30, height:30, borderRadius:'50%', background:'linear-gradient(135deg,var(--accent),var(--accent2))', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:12, flexShrink:0 }}>
                {initials}
              </div>
              <ChevronDown size={13} style={{ color:'var(--text3)', transition:'transform .2s', transform: dropOpen?'rotate(180deg)':'rotate(0deg)' }}/>
            </div>

            {dropOpen && (
              <div className="dropdown">
                <div className="dropdown-info">
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                    <div>
                      <div className="dropdown-name">{user?.first_name} {user?.last_name}</div>
                      <div className="dropdown-email">{user?.email}</div>
                      {user?.job_title && <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>{user.job_title}{user?.company ? ` · ${user.company}` : ''}</div>}
                    </div>
                    <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:8, background:planBg, color:planColor, textTransform:'uppercase', letterSpacing:.5, flexShrink:0 }}>
                      {user?.plan || 'free'}
                    </span>
                  </div>
                </div>

                <div className="dropdown-divider"/>

                <button className="dropdown-item" onClick={() => { navigate('/profile'); setDropOpen(false); }}>
                  <User size={14}/> My Profile
                </button>

                <button className="dropdown-item" onClick={() => { navigate('/agent'); setDropOpen(false); }}>
                  <Sparkles size={14}/> AI Agent
                  <span style={{ marginLeft:'auto', fontSize:9, fontWeight:800, padding:'1px 5px', borderRadius:4, background:'linear-gradient(135deg,#8b5cf6,#e11d48)', color:'#fff' }}>AI</span>
                </button>

                <button className="dropdown-item" onClick={() => { navigate('/team'); setDropOpen(false); }}>
                  <Users size={14}/> My Team
                  {!['pro','enterprise'].includes(user?.plan) && (
                    <span style={{ marginLeft:'auto', fontSize:10, fontWeight:700, padding:'1px 5px', borderRadius:4, background:'var(--accent)', color:'#fff' }}>PRO</span>
                  )}
                </button>

                {user?.role === 'admin' && (
                  <button className="dropdown-item" onClick={() => { navigate('/admin'); setDropOpen(false); }}>
                    <ShieldCheck size={14}/> Admin Panel
                  </button>
                )}

                {user?.role !== 'admin' && (
                  <div style={{ padding:'8px 16px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--text3)', marginBottom:5 }}>
                      <span>Search credits</span>
                      <span style={{ fontWeight:700, color:'var(--accent)' }}>{user?.search_credits ?? 0}</span>
                    </div>
                    <div style={{ height:5, background:'var(--bg3)', borderRadius:3, overflow:'hidden' }}>
                      <div style={{ height:'100%', borderRadius:3, background:'var(--accent)', width:`${Math.min(100, ((user?.search_credits||0)/50)*100)}%`, transition:'width .4s' }}/>
                    </div>
                  </div>
                )}

                <div className="dropdown-divider"/>

                <button className="dropdown-item danger" onClick={() => { logout(); navigate('/login'); }}>
                  <LogOut size={14}/> Sign Out
                </button>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="theme-toggle"
            onClick={() => setMobileOpen(o => !o)}
            style={{ display:'none' }}
            id="mobile-menu-btn"
            aria-label="Menu"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              {mobileOpen
                ? <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
                : <><rect y="2"  width="16" height="2" rx="1"/><rect y="7"  width="16" height="2" rx="1"/><rect y="12" width="16" height="2" rx="1"/></>
              }
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile slide-down menu */}
      {mobileOpen && (
        <div ref={mobileRef} style={{
          position:'fixed', top:62, left:0, right:0, zIndex:99,
          background:'var(--surface)', borderBottom:'1px solid var(--border)',
          boxShadow:'var(--shadow-lg)', padding:'8px 0 12px',
        }}>
          {NAV.map(n => (
            <button
              key={n.path}
              onClick={() => navigate(n.path)}
              style={{
                display:'flex', alignItems:'center', gap:10, width:'100%',
                padding:'11px 20px', background:'none', border:'none',
                fontSize:14, fontWeight: isActive(n.path)?700:500,
                color: isActive(n.path)?'var(--accent)':'var(--text2)',
                cursor:'pointer', textAlign:'left',
              }}
            >
              {n.icon} {n.label}
              {n.aiTag && <span style={{ marginLeft:4, fontSize:9, fontWeight:800, padding:'1px 5px', borderRadius:4, background:'linear-gradient(135deg,#8b5cf6,#e11d48)', color:'#fff' }}>AI</span>}
              {n.badge && !n.aiTag && <span style={{ marginLeft:4, fontSize:9, fontWeight:800, padding:'1px 5px', borderRadius:4, background:'var(--accent)', color:'#fff' }}>{n.badge}</span>}
            </button>
          ))}
          <div style={{ height:1, background:'var(--border)', margin:'8px 0' }}/>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'11px 20px', background:'none', border:'none', fontSize:14, color:'var(--red)', cursor:'pointer' }}
          >
            <LogOut size={15}/> Sign Out
          </button>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .navbar-nav { display: none !important; }
          #mobile-menu-btn { display: flex !important; }
          .navbar-right > div:first-child { display: none !important; }
        }
      `}</style>
    </>
  );
}